import { useEffect, useState } from "react";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchSession, sessionQueryKey, hasModule } from "@/lib/auth";
import { fmtDateTime } from "@/lib/dates";
import { applyTheme, getStoredPreference, resolveTheme } from "@/theme";
import { getCountSheet, worklistKeys } from "@/features/worklists/worklists.api";
import { CountSheetPicture } from "@/features/worklists/CountSheetPicture";
import "@/features/worklists/countSheet.css";

// The count sheet as a picture, on a page of its own: it is a sheet of paper to
// print, and the world bar and module tabs have no place on it. Outside _shell
// for that reason, with the same session guard as the mail window.
//
// The worklists screen sends you here right after the request; the pallets come
// from the WMS through DataBridge a moment later, so the page asks until they
// are in. Then the owners in the rows can be ticked, as many as wanted: the
// sheets count only theirs, and each lists the owners of its row with a box,
// crossed for the ones ticked. The ones ticked ride in the address
// (owners=7,95), so printing and reloading keep them.
//
// The paper is in English unless Dutch is chosen (paper=nl): FrieslandCampina and DFE Pharma
// audit the counts in English (Robert-Jan van der Zanden, 28-10-2026). The screen around it
// keeps the user's language.

const POLL_MS = 2000;
// After this the page still waits, but says that the answer is late.
const SLOW_AFTER_MS = 60_000;
// And after this it stops asking: whatever DataBridge does now, nobody is looking.
const GIVE_UP_AFTER_MS = 10 * 60_000;
// Owner numbers in the WMS, a comma between.
const OWNER_LIST = /^\d{1,6}(,\d{1,6})*$/;
type PaperLang = "nl" | "en";
// The time of the stock on the paper, in the paper's language: "28 Oct 2026, 14:08".
const stockTime = (d: Date, lang: PaperLang) =>
  new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "nl-NL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/Amsterdam",
  }).format(d);

export const Route = createFileRoute("/count-sheet")({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData({
      queryKey: sessionQueryKey,
      queryFn: fetchSession,
    });
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });
    return { session };
  },
  validateSearch: (search: Record<string, unknown>): { request: string; owners?: string; paper?: PaperLang } => ({
    request: typeof search["request"] === "string" ? search["request"] : "",
    // One owner comes back as a number: the address has no types.
    owners: OWNER_LIST.test(String(search["owners"] ?? "")) ? String(search["owners"]) : undefined,
    paper: search["paper"] === "nl" ? "nl" : undefined,
  }),
  component: CountSheetPage,
});

function CountSheetPage() {
  const { t, i18n } = useTranslation();
  const { request, owners: ownersParam = "", paper = "en" } = Route.useSearch();
  const tPaper = i18n.getFixedT(paper);
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const allowed = hasModule(session, "worklists");
  const [now, setNow] = useState(() => Date.now());

  // Paper has one look: the light theme while the sheet is open, the chosen one after.
  useEffect(() => {
    applyTheme("light");
    return () => applyTheme(resolveTheme(getStoredPreference()));
  }, []);

  const query = useQuery({
    queryKey: worklistKeys.countSheet(request, ownersParam),
    queryFn: () => getCountSheet(request, ownersParam),
    enabled: allowed && request !== "",
    // Ticking an owner draws the sheets again: keep the ones there until the new ones are in.
    placeholderData: keepPreviousData,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data || data.status !== "requested") return false;
      return Date.now() - new Date(data.requestedAt).getTime() < GIVE_UP_AFTER_MS ? POLL_MS : false;
    },
  });

  const data = query.data;
  const waiting = data?.status === "requested";
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => setNow(Date.now()), POLL_MS);
    return () => window.clearInterval(timer);
  }, [waiting]);
  const slow = waiting && now - new Date(data.requestedAt).getTime() > SLOW_AFTER_MS;

  // The owners in the rows, and the ones ticked among them. A request for one owner from before
  // 13-10-2026 has that owner's pallets only, and nothing to tick.
  const owner = data?.owner ?? null;
  const owners = data?.owners ?? null;
  const ticked = new Set(ownersParam.split(",").filter((n) => n !== ""));
  const ownerName = (o: { number: string; name: string | null }, tr = t) => o.name ?? tr("countSheet.ownerNumber", { number: o.number });
  // Ticked, in the order offered; a number the rows do not have (an address typed by hand) counts nothing,
  // and the sheet says whose it is all the same.
  const tickedOwners = owners
    ? [
        ...owners.filter((o) => ticked.has(o.number)),
        ...[...ticked].filter((n) => !owners.some((o) => o.number === n)).map((n) => ({ number: n, name: null })),
      ]
    : [];
  const ownerNames = owner ? [owner.name] : tickedOwners.map((o) => ownerName(o));
  // On the paper, with owners ticked: the owners of that row, in the order offered, the ticked ones crossed.
  const ownersOnSheet = (rowOwners: string[] | null) =>
    owners && ticked.size > 0
      ? owners
          .filter((o) => !rowOwners || rowOwners.includes(o.number))
          .map((o) => ({ number: o.number, name: ownerName(o, tPaper), counted: ticked.has(o.number) }))
      : [];
  const tick = (number: string, on: boolean) => {
    const next = new Set(ticked);
    if (on) next.add(number);
    else next.delete(number);
    // In the order offered, the most pallets first.
    const value = (owners ?? []).filter((o) => next.has(o.number)).map((o) => o.number).join(",");
    void navigate({ search: (prev) => ({ ...prev, owners: value || undefined }), replace: true });
  };

  // A sheet per row. One whose places WLX does not know has nothing to draw; with owners
  // ticked, a row without any of their pallets has nothing to count either.
  const sheets = data?.sheets ?? null;
  const unknownRows = (sheets ?? []).filter((s) => s.sheet.sections.length === 0).map((s) => s.row);
  const emptyRows =
    ownerNames.length > 0
      ? (sheets ?? []).filter((s) => s.sheet.sections.length > 0 && s.sheet.totalPallets === 0).map((s) => s.row)
      : [];
  const drawn = (sheets ?? []).filter((s) => !unknownRows.includes(s.row) && !emptyRows.includes(s.row));
  const drawable = drawn.length > 0;
  // For the office: where WLX and the WMS disagree, over all rows together.
  const notInWlx = (sheets ?? []).flatMap((s) => s.sheet.notInWlx.map((p) => p.name));
  const notInWms = drawn.reduce((sum, s) => sum + s.sheet.notInWms, 0);

  return (
    <div className="cs-page" data-screen-label="Back-office Count sheet">
      <div className="cs-bar">
        <div className="cs-bar__row">
          <Link to="/worklists" className="wl-btn wl-btn--ghost">
            {t("countSheet.back")}
          </Link>
          <p className="t-body-sm t-muted cs-bar__status" role="status">
            {data &&
              t("countSheet.requested", {
                name: data.requestedByName,
                when: fmtDateTime(new Date(data.requestedAt)),
              })}
            {data && drawable && ` · ${t("countSheet.sheets", { count: drawn.length })}`}
          </p>
          <label className="t-body-sm cs-bar__paper">
            {t("countSheet.paper")}
            <select
              className="wl-select"
              value={paper}
              onChange={(e) =>
                void navigate({
                  search: (prev) => ({ ...prev, paper: e.target.value === "nl" ? "nl" : undefined }),
                  replace: true,
                })
              }
            >
              <option value="en">{t("countSheet.paperEn")}</option>
              <option value="nl">{t("countSheet.paperNl")}</option>
            </select>
          </label>
          <button type="button" className="wl-btn wl-btn--primary" disabled={!drawable} onClick={() => window.print()}>
            {t("countSheet.print")}
          </button>
        </div>

        {!allowed && <div className="wl-alert wl-alert--danger">{t("countSheet.noAccess")}</div>}
        {allowed && request !== "" && query.isPending && <p className="t-body-sm t-muted">{t("common.loading")}</p>}
        {allowed && (request === "" || query.isError) && (
          <div className="wl-alert wl-alert--danger">
            {query.error instanceof Error ? query.error.message : t("countSheet.notFound")}
          </div>
        )}
        {waiting && (
          <div className={`wl-alert ${slow ? "wl-alert--warning" : "wl-alert--info"}`} role="status">
            {slow ? t("countSheet.slow") : t("countSheet.waiting")}
          </div>
        )}
        {data?.status === "failed" && (
          <div className="wl-alert wl-alert--danger" role="alert">
            <div>
              <strong>{t("countSheet.failed")}</strong>
              {data.message && <p className="t-body-sm">{data.message}</p>}
            </div>
          </div>
        )}
        {data && unknownRows.length > 0 && (
          <div className="wl-alert wl-alert--warning">
            {t("countSheet.noPlaces", { count: unknownRows.length, rows: unknownRows.join(", "), warehouse: data.warehouse.name })}
          </div>
        )}
        {owners && owners.length > 0 && (
          <fieldset className="cs-owners">
            <legend className="t-body-sm">
              <strong>{t("countSheet.owners")}</strong>{" "}
              <span className="t-muted">{ticked.size === 0 ? t("countSheet.ownersAll") : t("countSheet.ownersTicked")}</span>
            </legend>
            <div className="cs-owners__list">
              {owners.map((o) => (
                <label key={o.number} className="wl-choice-card cs-owners__owner">
                  <input type="checkbox" checked={ticked.has(o.number)} onChange={(e) => tick(o.number, e.target.checked)} />
                  <span className="wl-choice-card__body">
                    <span className="wl-choice-card__title">{ownerName(o)}</span>
                    <span className="wl-choice-card__meta">
                      {t("countSheet.ownerMeta", { number: o.number, count: o.pallets })}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {ticked.size > 0 && (
              <button
                type="button"
                className="wl-btn wl-btn--ghost wl-btn--sm"
                onClick={() => void navigate({ search: (prev) => ({ ...prev, owners: undefined }), replace: true })}
              >
                {t("countSheet.ownersReset")}
              </button>
            )}
          </fieldset>
        )}
        {data?.status === "received" && !owner && owners === null && (
          <p className="t-body-sm t-muted">{t("countSheet.ownersUnknown")}</p>
        )}
        {ownerNames.length > 0 && emptyRows.length > 0 && (
          <div className="wl-alert wl-alert--info">
            {t("countSheet.noOwnerPallets", { count: emptyRows.length, rows: emptyRows.join(", "), owner: ownerNames.join(", ") })}
          </div>
        )}
        {/* For the office, not for the paper: where WLX and the WMS disagree about the rows. */}
        {notInWlx.length > 0 && (
          <div className="wl-alert wl-alert--warning">
            {t("countSheet.notInWlx", { count: notInWlx.length, names: notInWlx.join(", ") })}
          </div>
        )}
        {drawable && notInWms > 0 && <div className="wl-alert wl-alert--warning">{t("countSheet.notInWms", { count: notInWms })}</div>}
      </div>

      {data && drawable && (
        <div className="cs-scroll">
          {drawn.map((s) => (
            <CountSheetPicture
              key={s.row}
              sheet={s.sheet}
              row={s.row}
              placePrefix={s.placePrefix}
              warehouseName={data.warehouse.name}
              owner={owner?.name ?? null}
              owners={ownersOnSheet(s.owners)}
              asOf={data.receivedAt ? stockTime(new Date(data.receivedAt), paper) : ""}
              lang={paper}
              t={tPaper}
            />
          ))}
        </div>
      )}
    </div>
  );
}
